import {useRouter} from 'next/router';
import {HTMLAttributes, useEffect, useMemo, useRef, useState} from 'react';
import {t} from '../../../../../../common/getTranslations';
import {requestContext} from '../../../../../../common/requestContext';
import {useWindowInnerHeight} from '../../../../../../common/shared/hooks/useWindowInnerHeight';
import {INIPlayer} from '../../../../../../common/shared/ini-player/INIPlayer.interface';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {PC_VIEWER_HEADER_HEIGHT} from '../../../../../../design-system/organisms/PcHeader/constants';
import {VodViewerData} from '../../../../../../graphql/generated/types';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../../../../header/constants';
import {ONLY_DATA_CONFIG} from '../../../../../../reactQuery/constant';
import {showToast} from '../../../../../../toast/toastHandler';
import {useViewerInfoQuery} from '../../../../../../wrapper/viewerWrapper';
import {VideoStatus} from '../constant/videoViewerConstant';
import {
  CONTENTS_PC_MAX_WIDTH,
  MAX_PROGRESS_RATE,
} from '../constant/viewerConstant';
import {useVideoViewerEvent} from '../hook/useVideoViewerEvent';
import {
  clearVideoViewerData,
  setIsHovered,
  setShowControlBox,
  setVideoState,
} from '../state/videoViewerSlice';
import {restoreFromLastViewRate} from '../util/restoreFromLastViewRate';
import {
  getMuteFromSessionStorage,
  getVolumeFromSessionStorage,
} from '../util/videoPlayerSessionStorage';
import {IniVideoPlayerContainer} from './IniVideoPlayerContainer';
import {VideoPlayerControlBoxContainer} from './VideoPlayerControlBoxContainer';
import {VideoPlayerIniErrorHandleContainer} from './VideoPlayerIniErrorHandleContainer';
import {VideoViewerProgressSyncContainer} from './VideoViewerProgressSyncContainer';

export interface VideoViewerContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  seriesId: number;
  productId: number;
  previewKey?: string;
  // CMS Preview를 위해 외부에서 주입해주는 필드입니다.
  isShortsDrama?: boolean;
}

// TODO: hasTrySchemeQuery 관련 처리를 해야 하는지 검토해야 합니다.
export function VideoViewerContainer({
  seriesId,
  productId,
  children,
  previewKey,
  isShortsDrama: _isShortsDrama,
}: VideoViewerContainerProps) {
  const {
    isFromPrevProduct,
    showControlBox,
    videoStatus,
    shortVodViewerPreview,
  } = useTypedSelector(state => ({
    shortVodViewerPreview: state.viewerInfo.shortVodViewerPreview,
    isFromPrevProduct: state.viewerInfo.isFromPrevProduct,
    showControlBox: state.videoViewer.showControlBox,
    videoStatus: state.videoViewer.videoState.videoStatus,
  }));
  const {data} = useViewerInfoQuery(
    {seriesId, productId},
    previewKey ? ONLY_DATA_CONFIG : {}
  );
  const viewerInfoData = data;
  const viewerData = previewKey
    ? (shortVodViewerPreview[previewKey].data?.viewerData as VodViewerData)
    : (viewerInfoData?.viewerData as VodViewerData);
  const lastViewInfo = viewerInfoData?.item?.serviceProperty?.lastViewInfo;
  const isShortsDrama = _isShortsDrama ?? !!viewerInfoData?.item?.isShortsDrama;
  const hasNextEpisode = !!viewerInfoData?.nextItem?.productId;

  const dispatch = useTypedDispatch();
  const {isMobile} = requestContext.requestInfo.display;
  const videoRef = useRef<INIPlayer>(null);
  const [timeUpdateHandlerEnabled, setTimeUpdateHandlerEnabled] =
    useState(true);
  const router = useRouter();

  // currentTime 및 loadedProgress는 redux store에 저장하기에는 너무 자주 변경되는 값이므로
  // 내부적으로 state로 관리합니다.
  const [currentTime, setCurrentTime] = useState(0);
  const [loadedProgress, setLoadedProgress] = useState(0);
  const windowInnerHeight = useWindowInnerHeight();

  useVideoViewerEvent(isShortsDrama);

  useEffect(() => {
    const isMute = getMuteFromSessionStorage();
    if (isMute !== undefined) {
      dispatch(setVideoState({isMute}));
    }

    const volume = getVolumeFromSessionStorage();
    if (volume !== undefined) {
      dispatch(setVideoState({volume}));
    }
  }, [dispatch]);

  const startTime = useMemo(() => {
    //이전화에서 넘어왔을 경우 처음부터 시작합니다
    if (isFromPrevProduct) {
      return 0;
    }

    const duration = viewerData?.vodDownloadData?.duration;
    if (!duration) {
      return 0;
    }

    const lastViewTime = restoreFromLastViewRate({
      rate: (lastViewInfo && lastViewInfo.rate) || 0,
      total: duration / 1000,
    });

    // 영상 뷰어의 경우 마지막 페이지 기록에서(영상 마지막에만) 10초 전 영상의 위치로 이동합니다.
    if (lastViewInfo?.rate === MAX_PROGRESS_RATE) {
      return lastViewTime - 10 > 0 ? lastViewTime - 10 : 0;
    }

    return lastViewTime;
  }, [isFromPrevProduct, lastViewInfo, viewerData?.vodDownloadData?.duration]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: productId 변경마다 초기화 되어야함
  useEffect(() => {
    setCurrentTime(startTime);
  }, [startTime, productId]);

  useEffect(() => {
    return () => {
      dispatch(clearVideoViewerData(productId));
    };
  }, [dispatch, productId]);

  useEffect(() => {
    if (isShortsDrama && !hasNextEpisode && videoStatus === VideoStatus.Ended) {
      showToast({
        message: t('viewer_menu_last_episode'),
        isMobile,
      });
    }
  }, [isShortsDrama, videoStatus, isMobile, hasNextEpisode]);

  if (!viewerData?.vodDownloadData) {
    return null;
  }

  const {vodDownloadData} = viewerData;
  const {width, height, duration, endpointUrl} = vodDownloadData;

  const videoMetadata = {
    videoWidth: width,
    videoHeight: height,
    duration: duration / 1000,
    url: endpointUrl,
  };

  return (
    <div
      className={twMerge(
        'flex w-full flex-col',
        isMobile
          ? isShortsDrama
            ? 'pt-mobile_header_height_px'
            : 'py-mobile_header_height_px'
          : isShortsDrama
            ? 'pt-pc_viewer_header_height_px'
            : 'py-pc_viewer_header_height_px'
      )}
    >
      <div
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={twMerge(
          'video-viewer-container',
          'relative mx-auto block w-full after:block'
        )}
        style={{
          maxWidth: !isMobile ? CONTENTS_PC_MAX_WIDTH : undefined,
          height: isShortsDrama
            ? windowInnerHeight -
              (isMobile ? MOBILE_HEADER_HEIGHT_PX : PC_VIEWER_HEADER_HEIGHT)
            : 'auto',
        }}
        onClick={e => {
          e.stopPropagation();
          // PC에서는 마우스 호버로 동작함
          if (isMobile && !isShortsDrama) {
            dispatch(setShowControlBox(!showControlBox));
          }
        }}
        onTouchStart={!isShortsDrama ? e => e.stopPropagation() : undefined}
        onTouchEnd={!isShortsDrama ? e => e.stopPropagation() : undefined}
        onMouseOver={() => {
          dispatch(setIsHovered(true));
          if (!isMobile && !showControlBox) {
            dispatch(setShowControlBox(true));
          }
        }}
        onMouseLeave={() => {
          dispatch(setIsHovered(false));
          if (!isMobile && videoStatus === VideoStatus.Playing) {
            dispatch(setShowControlBox(false));
          }
        }}
      >
        {viewerData && (
          <IniVideoPlayerContainer
            hasNextEpisode={hasNextEpisode}
            isShortsDrama={isShortsDrama}
            videoRef={videoRef}
            drmInfo={viewerData.drmInfo}
            videoMetadata={videoMetadata}
            timeUpdateHandlerEnabled={timeUpdateHandlerEnabled}
            startTime={startTime}
            setCurrentTime={setCurrentTime}
            setLoadedProgress={setLoadedProgress}
          />
        )}
        <VideoPlayerControlBoxContainer
          videoRef={videoRef}
          productId={productId}
          currentTime={currentTime}
          loadedProgress={loadedProgress}
          duration={videoMetadata.duration}
          setTimeUpdateHandlerEnabled={setTimeUpdateHandlerEnabled}
          setCurrentTime={setCurrentTime}
          isShortsDrama={isShortsDrama}
          hasNextEpisode={hasNextEpisode}
        />
        <VideoPlayerIniErrorHandleContainer
          productId={productId}
          url={videoMetadata.url}
          retryIniPlayer={() => videoRef.current?.reload()}
          className="absolute top-0 left-0"
        />

        <VideoViewerProgressSyncContainer
          seriesId={seriesId}
          productId={productId}
          currentTime={currentTime}
        />
      </div>
      <div
        className={!isMobile ? 'mx-auto w-full' : ''}
        style={{
          //PC는 뷰어앤드도 최대 너비가 존재합니다. 최대크기와 중앙에 위치하게 마진을 줍니다.
          maxWidth: !isMobile ? CONTENTS_PC_MAX_WIDTH : undefined,
        }}
      >
        {children}
      </div>
      {/* PC 버전에선 최대너비 16:9 비율 고정 */}
      <style jsx>
        {`
          .video-viewer-container::after {
            padding-top: ${
              isShortsDrama
                ? 0
                : !isMobile
                  ? 'calc(100% / (16/9))'
                  : width && height
                    ? `${(height / width) * 100}%`
                    : 'calc(100vw / (16/9))'
            };
          }
        `}
      </style>
    </div>
  );
}
